import React, { useState } from 'react';
import Dropdown from '../Dropdown';
import { AddButton, DeleteButton, SortButton } from '../buttons';
import { useTranslation } from 'react-i18next';
const directionOptions = [{
  value: 'asc',
  text: 'A -> Z'
}, {
  value: 'desc',
  text: 'Z -> A'
}];

// headers is the table header list. MultiColumnSort expects each header to have:
// { text: string, sortable?: boolean }. Only headers with sortable: true can be selected.
// sortColumns use headerIndex to point back to the selected header's position in this array.
export default function MultiColumnSort(_ref) {
  let {
    headers,
    onSort,
    isExpanded
  } = _ref;
  const [selectedHeaderIndex, setSelectedHeaderIndex] = useState('');
  // Selected sort columns in priority order.
  // Each item is { headerIndex: number, direction: 'asc' | 'desc' }.
  const [sortColumns, setSortColumns] = useState([]);
  const [draggedSortColumnIndex, setDraggedSortColumnIndex] = useState(null);
  const {
    t
  } = useTranslation();
  const unusedHeaders = headers.map((header, headerIndex) => ({
    header,
    headerIndex
  })).filter(_ref2 => {
    let {
      header,
      headerIndex
    } = _ref2;
    return header.sortable && !sortColumns.some(sortColumn => sortColumn.headerIndex === headerIndex);
  });
  const addSortColumn = () => {
    if (selectedHeaderIndex === '') return;
    setSortColumns([...sortColumns, {
      headerIndex: Number(selectedHeaderIndex),
      direction: 'asc'
    }]);
    setSelectedHeaderIndex('');
  };
  const changeSortColumnDirection = (headerIndex, direction) => {
    setSortColumns(sortColumns.map(sortColumn => sortColumn.headerIndex === headerIndex ? {
      ...sortColumn,
      direction
    } : sortColumn));
  };
  const removeSortColumn = headerIndex => {
    setSortColumns(sortColumns.filter(sortColumn => sortColumn.headerIndex !== headerIndex));
  };
  const moveSortColumn = targetSortColumnIndex => {
    if (draggedSortColumnIndex === null || draggedSortColumnIndex === targetSortColumnIndex) return;
    setSortColumns(currentSortColumns => {
      const updatedSortColumns = [...currentSortColumns];
      const [draggedSortColumn] = updatedSortColumns.splice(draggedSortColumnIndex, 1);
      updatedSortColumns.splice(targetSortColumnIndex, 0, draggedSortColumn);
      return updatedSortColumns;
    });
    setDraggedSortColumnIndex(null);
  };
  const sortColumnDragOver = event => {
    event.preventDefault();
  };
  const sortColumnDragEnd = () => {
    setDraggedSortColumnIndex(null);
  };
  if (!isExpanded) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "MultiColumnSort"
  }, /*#__PURE__*/React.createElement("div", {
    className: "border rounded p-3 mt-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center gap-2 mb-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex-grow-1"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    "aria-label": "Sort header",
    dataTestId: "multi-column-sort-header",
    dropdownStyle: {
      width: '100%',
      minWidth: 0
    },
    options: unusedHeaders.map(_ref3 => {
      let {
        header,
        headerIndex
      } = _ref3;
      return {
        key: headerIndex,
        value: headerIndex,
        text: header.text
      };
    }),
    value: selectedHeaderIndex,
    onChange: setSelectedHeaderIndex
  })), /*#__PURE__*/React.createElement(AddButton, {
    "data-testid": "multi-column-sort-add-button",
    type: "button",
    style: {
      minWidth: '120px'
    },
    onClick: addSortColumn,
    disabled: selectedHeaderIndex === ''
  })), sortColumns.map((sortColumn, index) => /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center gap-2 mb-2",
    "data-testid": `multi-column-sort-row-${sortColumn.headerIndex}`,
    key: sortColumn.headerIndex,
    onDragOver: sortColumnDragOver,
    onDrop: () => moveSortColumn(index)
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18",
    "data-testid": `multi-column-sort-drag-handle-${sortColumn.headerIndex}`,
    draggable: true,
    onDragEnd: sortColumnDragEnd,
    onDragStart: () => setDraggedSortColumnIndex(index),
    style: {
      cursor: 'grab'
    }
  }, "drag_indicator"), /*#__PURE__*/React.createElement("span", {
    "data-testid": `multi-column-sort-priority-label-${sortColumn.headerIndex}`,
    style: {
      minWidth: '70px'
    }
  }, index === 0 ? 'Sort by' : 'Then by'), /*#__PURE__*/React.createElement("span", {
    className: "flex-grow-1",
    "data-testid": `multi-column-sort-header-label-${sortColumn.headerIndex}`,
    style: {
      minWidth: '200px'
    }
  }, t(headers[sortColumn.headerIndex].text)), /*#__PURE__*/React.createElement(Dropdown, {
    "aria-label": `Sort direction for ${headers[sortColumn.headerIndex].text}`,
    dataTestId: `multi-column-sort-direction-${sortColumn.headerIndex}`,
    dropdownStyle: {
      width: '110px',
      minWidth: '110px'
    },
    options: directionOptions,
    showNullOption: false,
    value: sortColumn.direction,
    onChange: direction => changeSortColumnDirection(sortColumn.headerIndex, direction)
  }), /*#__PURE__*/React.createElement(DeleteButton, {
    "aria-label": `Remove ${headers[sortColumn.headerIndex].text} sorting`,
    "data-testid": `multi-column-sort-remove-${sortColumn.headerIndex}`,
    type: "button",
    onClick: () => removeSortColumn(sortColumn.headerIndex)
  }))), /*#__PURE__*/React.createElement(SortButton, {
    className: "mt-2",
    "data-testid": "multi-column-sort-submit-button",
    disabled: sortColumns.length === 0
    // onSort receives the same sortColumns format:
    // [{ headerIndex: number, direction: 'asc' | 'desc' }]
    ,
    onClick: () => onSort(sortColumns)
  })));
}